import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Box,
  useMediaQuery,
  useTheme,
  Stack,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Collapse,
  Tooltip,
  Chip,
  Switch,
  FormControlLabel,
  TablePagination,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { format, isWithinInterval, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import pdfConfig from '../utils/pdfConfig';
import * as XLSX from 'xlsx';
import ActaPDF from './ActaPDF';
import SignatureManager from './SignatureManager';
import historialActasStyles from '../styles/historialActasStyles';
import styles from '../styles/historialActasStyles';
import { drawerWidth } from '../constants';
import { approveActa } from '../services/approvalService';
import { hasRole, ROLES } from '../userRoles';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { sendEmail } from '../services/emailService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Badge,
  CircularProgress
} from '@mui/material';


const HistorialActas = ({ actas, userId, userEmail, onEditActa, userRole, onActaUpdate, onActaDeleted }) => {
  const theme = useTheme();
  const styles = historialActasStyles(theme);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showAllActas, setShowAllActas] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showPendingApproval, setShowPendingApproval] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [selectedActaForSignature, setSelectedActaForSignature] = useState(null);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedActaComments, setSelectedActaComments] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [processingComment, setProcessingComment] = useState(false);
  const [approvalPermissions, setApprovalPermissions] = useState({});
  const [generatingPdfActaId, setGeneratingPdfActaId] = useState(null);
  
  // Estados para paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Calcular permisos de aprobación de forma asíncrona
  useEffect(() => {
    const calculateApprovalPermissions = async () => {
      console.log('=== CALCULANDO PERMISOS DE APROBACIÓN ===');
      console.log('Total actas:', actas.length);
      console.log('UserEmail:', userEmail);
      console.log('UserRole:', userRole);
      
      const permissions = {};
      
      for (const acta of actas) {
        console.log(`Procesando acta ${acta.id}:`, {
          numeroActa: acta.numeroActa,
          isExternal: acta.isExternal,
          approvalStatus: acta.approvalStatus
        });
        
        if (!acta.isExternal && acta.approvalStatus !== 'borrador' && acta.approvalStatus === 'por aprobar') {
          try {
            const canApprove = await canUserApproveActa(acta, userEmail, userRole);
            console.log(`Acta ${acta.numeroActa} - Puede aprobar:`, canApprove);
            permissions[acta.id] = canApprove;
          } catch (error) {
            console.error('Error calculating approval permissions for acta:', acta.id, error);
            permissions[acta.id] = false;
          }
        } else {
          console.log(`Acta ${acta.numeroActa} - No cumple condiciones para aprobación`);
          permissions[acta.id] = false;
        }
      }
      
      console.log('Permisos calculados:', permissions);
      setApprovalPermissions(permissions);
    };
    
    if (actas.length > 0 && userEmail) {
      calculateApprovalPermissions();
    }
  }, [actas, userEmail, userRole, userId]);
  
  // Función para manejar la eliminación de actas borrador
  const handleDeleteDraft = async (acta) => {
    try {
      // Eliminar el acta de Firestore
      await deleteDoc(doc(db, 'actas', acta.id));
      
      // Preparar detalles del acta para el log
      const actaDetails = {
        numeroActa: acta.numeroActa,
        tipoActa: acta.tipoActa,
        tipoProyecto: acta.tipoProyecto,
        productoProyecto: acta.productoProyecto || ''
      };
      
      // Notificar al componente padre que se ha eliminado un acta
      if (onActaDeleted && typeof onActaDeleted === 'function') {
        onActaDeleted(acta.id, actaDetails);
      }
      

    } catch (error) {
      console.error('Error al eliminar acta borrador:', error);
      alert('Error al eliminar el borrador. Por favor intente nuevamente.');
    }
  };
  
  // Función para manejar la descarga de PDF
  const handlePDFDownload = async (acta) => {
    try {
      // Si es un acta externa, descargar el PDF original
      if (acta.isExternal && (acta.pdfUrl || acta.pdfBase64)) {
        console.log('Descargando PDF externo:', { pdfUrl: acta.pdfUrl, hasPdfBase64: !!acta.pdfBase64 });
        
        let downloadUrl;
        let shouldCleanupBlob = false;
        
        // Verificar si pdfUrl es válido y accesible
        if (acta.pdfUrl && acta.pdfUrl.startsWith('http')) {
          // Para URLs HTTP, usar directamente
          downloadUrl = acta.pdfUrl;
          console.log('Usando pdfUrl HTTP:', downloadUrl);
        } else if (acta.pdfBase64) {
          // Siempre usar base64 si está disponible para evitar problemas con blob URLs corruptos
          console.log('Usando base64 en lugar de blob URL potencialmente corrupto');
          // Convertir base64 a blob URL si pdfUrl no está disponible
          console.log('Convirtiendo base64 a blob...');
          
          try {
            // Manejar diferentes formatos de base64
            let base64Data = acta.pdfBase64;
            if (base64Data.includes(',')) {
              base64Data = base64Data.split(',')[1];
            }
            
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            downloadUrl = URL.createObjectURL(blob);
            shouldCleanupBlob = true;
            console.log('Blob URL creado exitosamente:', downloadUrl);
          } catch (base64Error) {
            console.error('Error al convertir base64:', base64Error);
            alert('Error al procesar el archivo PDF. El formato base64 puede estar corrupto.');
            return;
          }
        } else {
          console.error('No se encontró pdfUrl ni pdfBase64 válidos');
          alert('Error: No se puede descargar el PDF. Datos no disponibles.');
          return;
        }
        
        // Crear y ejecutar la descarga
        const downloadPDF = () => {
          try {
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `acta${acta.numeroActa}_${acta.tipoProyecto || 'sin-contrato'}_${acta.productoProyecto || 'sin-proyecto'}_${acta.fecha.toLocaleDateString('es-ES').replace(/\//g, '-')}.pdf`;
            link.style.display = 'none';
            document.body.appendChild(link);
            
            console.log('Iniciando descarga con nombre:', link.download);
            link.click();
            
            // Limpiar después de la descarga
            setTimeout(() => {
              document.body.removeChild(link);
              if (shouldCleanupBlob) {
                URL.revokeObjectURL(downloadUrl);
                console.log('Blob URL limpiado');
              }
            }, 100);
            
          } catch (downloadError) {
            console.error('Error en descarga directa:', downloadError);
            alert('Error al descargar el PDF. El archivo puede estar corrupto o no disponible.');
          }
        };
        
        downloadPDF();
        return;
      }
      
      // Para actas normales, asegurar que el estado de aprobación esté definido
      if (!acta.approvalStatus) {
        acta.approvalStatus = 'por aprobar';
      }

      if (generatingPdfActaId) {
        return;
      }

      setGeneratingPdfActaId(acta.id);
      try {
        const pdfBlob = await pdfConfig.generatePdfBlob(<ActaPDF data={acta} />);
        const downloadUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `acta${acta.numeroActa}_${acta.tipoProyecto || 'sin-contrato'}_${acta.productoProyecto || 'sin-proyecto'}_${acta.fecha.toLocaleDateString('es-ES').replace(/\//g, '-')}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(downloadUrl);
        }, 100);
      } finally {
        setGeneratingPdfActaId(null);
      }
    } catch (error) {
      console.error('Error al generar PDF:', error);
      alert('Error al descargar el PDF. Por favor, intente nuevamente.');
    }
  };

  // Función para abrir el diálogo de comentarios
  const handleOpenComments = (acta) => {
    setSelectedActaComments(acta);
    setCommentsDialogOpen(true);
  };

  // Función para aprobar un comentario
  const handleApproveComment = async (comment, acta) => {
    setProcessingComment(true);
    try {
      // Actualizar el estado del comentario en Firebase
      const actaRef = doc(db, 'actas', acta.id);
      const actaDoc = await getDoc(actaRef);
      
      if (!actaDoc.exists()) {
        throw new Error('El acta no existe');
      }
      
      const actaData = actaDoc.data();
      const comentarios = actaData.comentarios || [];
      
      const comentariosActualizados = comentarios.map(c => {
        if (c.fecha.toDate().getTime() === comment.fecha.toDate().getTime() && 
            c.asistente === comment.asistente) {
          return { ...c, estado: 'aprobado', fechaAprobacion: new Date() };
        }
        return c;
      });
      
      await updateDoc(actaRef, {
        comentarios: comentariosActualizados
      });
      
      // Enviar email de aprobación
      const emailSubject = `[IETS GESTOR ACTAS] Comentario Aprobado - Acta ${acta.numeroActa}`;
      const emailBody = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #4caf50;">✅ Comentario Aprobado</h2>
          <p>Estimado/a <strong>${comment.asistente}</strong>,</p>
          <p>Nos complace informarle que su comentario sobre el Acta <strong>${acta.numeroActa}</strong> ha sido <strong style="color: #4caf50;">aprobado</strong>.</p>
          
          <div style="background-color: #e8f5e8; padding: 15px; border-radius: 5px; margin: 20px 0; border-left: 4px solid #4caf50;">
            <h3 style="margin-top: 0; color: #2e7d32;">Su Comentario:</h3>
            <p style="font-style: italic; margin: 10px 0;">"${comment.comentario}"</p>
          </div>
          
          <div style="background-color: #f0f8ff; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <p style="margin: 0; font-weight: bold; color: #1976d2;">🎉 ¡Excelente!</p>
            <p style="margin: 5px 0 0 0;">Su comentario ha sido revisado y aprobado. Ahora puede proceder con la firma del acta.</p>
          </div>
          
          <p>Por favor, proceda a validar y firmar el acta utilizando el siguiente enlace:</p>
          
          <div style="text-align: center; margin: 25px 0;">
            <a href="${window.location.origin}/firma/${acta.id}" style="background-color: #4caf50; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">📝 Firmar Acta Ahora</a>
          </div>
          
          <div style="background-color: #fff3cd; padding: 15px; border-radius: 5px; margin: 20px 0; border-left: 4px solid #ffc107;">
            <p style="margin: 0; font-weight: bold; color: #856404;">📋 Recordatorio:</p>
            <p style="margin: 5px 0 0 0;">Para completar el proceso, es necesario que firme digitalmente el acta. Para más información sobre el proceso de firma, consulte el manual de usuario.</p>
          </div>
          
          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #666;">
            <p>Este es un correo automático, por favor no responda a este mensaje.</p>
            <p>Instituto de Evaluación Tecnológica en Salud - IETS</p>
          </div>
        </div>
      `;
      
      await sendEmail(comment.email, emailSubject, emailBody);
      
      // Actualizar el estado local
      setSelectedActaComments(prev => ({
        ...prev,
        comentarios: comentariosActualizados
      }));
      
      // Notificar al componente padre para actualizar la lista
      if (onActaUpdate) {
        onActaUpdate();
      }
      
    } catch (error) {
      console.error('Error al aprobar comentario:', error);
      alert('Error al aprobar el comentario: ' + error.message);
    } finally {
      setProcessingComment(false);
    }
  };

  // Función para rechazar un comentario
  const handleRejectComment = (comment) => {
    setSelectedComment(comment);
    setShowRejectDialog(true);
  };

  // Función para confirmar el rechazo del comentario
  const handleConfirmReject = async () => {
    if (!rejectReason.trim()) {
      alert('Por favor, proporcione una razón para el rechazo.');
      return;
    }
    
    setProcessingComment(true);
    try {
      // Actualizar el estado del comentario en Firebase
      const actaRef = doc(db, 'actas', selectedActaComments.id);
      const actaDoc = await getDoc(actaRef);
      
      if (!actaDoc.exists()) {
        throw new Error('El acta no existe');
      }
      
      const actaData = actaDoc.data();
      const comentarios = actaData.comentarios || [];
      
      const comentariosActualizados = comentarios.map(c => {
        if (c.fecha.toDate().getTime() === selectedComment.fecha.toDate().getTime() && 
            c.asistente === selectedComment.asistente) {
          return { 
            ...c, 
            estado: 'rechazado', 
            fechaRechazo: new Date(),
            razonRechazo: rejectReason.trim()
          };
        }
        return c;
      });
      
      await updateDoc(actaRef, {
        comentarios: comentariosActualizados
      });
      
      // Enviar email de rechazo
      const emailSubject = `[IETS GESTOR ACTAS] Comentario Rechazado - Acta ${selectedActaComments.numeroActa}`;
      const emailBody = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #f44336;">❌ Comentario Rechazado</h2>
          <p>Estimado/a <strong>${selectedComment.asistente}</strong>,</p>
          <p>Su comentario sobre el Acta <strong>${selectedActaComments.numeroActa}</strong> ha sido <strong style="color: #f44336;">rechazado</strong>.</p>
          
          <div style="background-color: #fff3e0; padding: 15px; border-radius: 5px; margin: 20px 0; border-left: 4px solid #ff9800;">
            <h3 style="margin-top: 0; color: #e65100;">Su Comentario Original:</h3>
            <p style="font-style: italic; margin: 10px 0; background-color: #f5f5f5; padding: 10px; border-radius: 3px;">"${selectedComment.comentario}"</p>
          </div>
          
          <div style="background-color: #ffebee; padding: 15px; border-radius: 5px; margin: 20px 0; border-left: 4px solid #f44336;">
             <h3 style="margin-top: 0; color: #c62828;">Motivo del Rechazo:</h3>
             <p style="margin: 10px 0; color: #d32f2f;">${rejectReason.trim()}</p>
           </div>
           
           <div style="background-color: #e3f2fd; padding: 15px; border-radius: 5px; margin: 20px 0;">
             <p style="margin: 0; font-weight: bold; color: #1976d2;">💡 ¿Qué puede hacer ahora?</p>
             <p style="margin: 5px 0 0 0;">Aunque su comentario fue rechazado, puede realizar un nuevo comentario o proceder directamente a firmar el acta si está de acuerdo con el contenido actual.</p>
           </div>
          
          <div style="text-align: center; margin: 25px 0;">
            <a href="${window.location.origin}/firma/${selectedActaComments.id}" style="background-color: #1976d2; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block; margin: 5px;">📝 Ir a Firmar o Comentar</a>
          </div>
          
          <div style="background-color: #f0f8ff; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <p style="margin: 0; font-weight: bold; color: #1976d2;">🔄 Opciones Disponibles:</p>
            <ul style="margin: 10px 0; color: #1976d2;">
              <li><strong>Realizar un nuevo comentario</strong> con las correcciones sugeridas</li>
              <li><strong>Firmar el acta directamente</strong> si está conforme con el contenido actual</li>
              <li><strong>Revisar el acta completa</strong> antes de tomar una decisión</li>
            </ul>
          </div>
          
          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #666;">
            <p>Este es un correo automático, por favor no responda a este mensaje.</p>
            <p>Instituto de Evaluación Tecnológica en Salud - IETS</p>
          </div>
        </div>
      `;
      
      await sendEmail(selectedComment.email, emailSubject, emailBody);
      
      // Actualizar el estado local
      setSelectedActaComments(prev => ({
        ...prev,
        comentarios: comentariosActualizados
      }));
      
      // Notificar al componente padre para actualizar la lista
      if (onActaUpdate) {
        onActaUpdate();
      }
      
      // Cerrar diálogos y limpiar estados
      setShowRejectDialog(false);
      setRejectReason('');
      setSelectedComment(null);
      
    } catch (error) {
      console.error('Error al rechazar comentario:', error);
      alert('Error al rechazar el comentario: ' + error.message);
    } finally {
      setProcessingComment(false);
    }
  };
  
  // Función para exportar a Excel
  const exportToExcel = () => {
    // Preparar los datos para exportar
    const dataToExport = filteredActas.map(acta => ({
      'Número de Acta': acta.numeroActa,
      'Tipo': acta.tipoActa,
      'Fecha': format(acta.fecha, 'dd/MM/yyyy', { locale: es }),
      'Proyecto': acta.tipoProyecto || '',
      'Producto': acta.productoProyecto || '',
      'Responsable': acta.responsable,
      'Creado por': acta.creatorEmail || '',
      'Objetivo': typeof acta.objetivo === 'object' && acta.objetivo !== null
        ? (acta.objetivo.textFallback || '')
        : (typeof acta.objetivo === 'string' && acta.objetivo.trim().startsWith('{')
          ? (() => {
              try {
                const p = JSON.parse(acta.objetivo);
                return p.textFallback || acta.objetivo;
              } catch (e) {
                return acta.objetivo;
              }
            })()
          : (acta.objetivo || '')),
      'Lugar': acta.lugar || '',
      'Hora Inicio': acta.horaInicio ? format(acta.horaInicio, 'HH:mm', { locale: es }) : '',
      'Hora Fin': acta.horaFinal ? format(acta.horaFinal, 'HH:mm', { locale: es }) : ''
    }));
    
    // Crear libro de Excel
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Historial de Actas');
    
    // Generar nombre de archivo
    const fileName = `historial_actas_${format(new Date(), 'dd-MM-yyyy')}.xlsx`;
    
    // Descargar archivo
    XLSX.writeFile(workbook, fileName);
  };
  
  // Estados para los filtros
  const [filters, setFilters] = useState({
    aprobador: '',
    tipoActa: '',
    proyecto: '',
    producto: '',
    fechaInicio: null,
    fechaFin: null,
    approvalStatus: ''
  });
  
  // Extraer proyectos, productos y aprobadores únicos de las actas
  const [proyectos, setProyectos] = useState([]);
  const [productos, setProductos] = useState([]);
  const [aprobadores, setAprobadores] = useState([]);
  const [tiposActa, setTiposActa] = useState(['Reunión', 'Comité', 'Otro']);
  
  // Extraer datos únicos para los filtros
  useEffect(() => {
    // Extraer proyectos únicos
    const uniqueProyectos = [...new Set(actas.map(acta => acta.tipoProyecto))]
      .filter(Boolean)
      .sort();
    setProyectos(uniqueProyectos);
    
    // Extraer aprobadores únicos (buscar en encargadosAprobacion o customProjectEmail)
    const allAprobadores = actas.flatMap(acta => {
      if (acta.encargadosAprobacion && acta.encargadosAprobacion.length > 0) {
        return acta.encargadosAprobacion.map(e => e.email);
      }
      return acta.customProjectEmail ? [acta.customProjectEmail] : [];
    });
    
    const uniqueAprobadores = [...new Set(allAprobadores)]
      .filter(Boolean)
      .sort();
    setAprobadores(uniqueAprobadores);
    
    // Resetear productos si cambia el proyecto
    if (filters.proyecto) {
      const productosDelProyecto = actas
        .filter(acta => acta.tipoProyecto === filters.proyecto)
        .map(acta => acta.productoProyecto)
        .filter(Boolean);
      const uniqueProductos = [...new Set(productosDelProyecto)].sort();
      setProductos(uniqueProductos);
    } else {
      setProductos([]);
    }
  }, [actas, filters.proyecto]);
  
  // Manejar cambios en los filtros
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Si se cambia el proyecto, resetear el producto
    if (name === 'proyecto' && value !== filters.proyecto) {
      setFilters(prev => ({
        ...prev,
        producto: ''
      }));
    }
  };
  
  // Manejar cambio en el switch de mostrar actas pendientes de aprobación
  const handlePendingApprovalChange = (e) => {
    setShowPendingApproval(e.target.checked);
    if (e.target.checked) {
      setFilters(prev => ({
        ...prev,
        approvalStatus: 'por aprobar'
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        approvalStatus: ''
      }));
    }
  };
  
  // Helper para comparar correos electrónicos de forma flexible (insensible a mayúsculas y tolerante a variaciones/truncamientos de usuario)
  const areEmailsMatching = (email1, email2) => {
    if (!email1 || !email2) return false;
    const e1 = String(email1).toLowerCase().trim();
    const e2 = String(email2).toLowerCase().trim();
    if (e1 === e2) return true;

    const [u1, d1] = e1.split('@');
    const [u2, d2] = e2.split('@');
    if (d1 && d2 && d1 === d2 && u1 && u2) {
      if (u1.startsWith(u2) || u2.startsWith(u1)) {
        return true;
      }
    }
    return false;
  };

  // Función para verificar permisos de aprobación
  const canUserApproveActa = async (acta, userEmail, userRole) => {
    try {
      if (!acta || !userEmail) return false;

      console.log(`=== VERIFICANDO PERMISOS PARA ACTA ${acta.numeroActa || acta.id} ===`);
      console.log('UserEmail:', userEmail);
      console.log('UserRole:', userRole);

      // 1. Si es administrador, puede aprobar cualquier acta
      if (hasRole(userRole, ROLES.ADMIN)) {
        console.log('Usuario es ADMIN - Retornando true');
        return true;
      }

      // 2. Verificar si el usuario está en encargadosAprobacion del acta
      if (acta.encargadosAprobacion && acta.encargadosAprobacion.length > 0) {
        const isAssigned = acta.encargadosAprobacion.some(encargado => 
          encargado.email && areEmailsMatching(encargado.email, userEmail)
        );
        if (isAssigned) {
          console.log('Usuario asignado en encargadosAprobacion del acta - Retornando true');
          return true;
        }
      }

      // 3. Verificar customProjectEmail del acta (aprobador asignado al acta)
      if (acta.customProjectEmail) {
        if (areEmailsMatching(acta.customProjectEmail, userEmail)) {
          console.log(`Usuario coincide con customProjectEmail (${acta.customProjectEmail}) - Retornando true`);
          return true;
        }
      }

      // 4. Consultar el correo del aprobador configurado en la base de datos para los proyectos/productos del acta
      const actaProjects = acta.tipoProyectos && acta.tipoProyectos.length > 0
        ? acta.tipoProyectos
        : (acta.tipoProyecto ? [acta.tipoProyecto] : []);
      const actaProducts = acta.productosProyecto && acta.productosProyecto.length > 0
        ? acta.productosProyecto
        : (acta.productoProyecto ? [acta.productoProyecto] : []);

      if (actaProjects.length > 0) {
        try {
          const { getContractProjectEmailWithFallback } = await import('../services/projectsApi');
          const configuredEmail = await getContractProjectEmailWithFallback(actaProjects, actaProducts);
          if (configuredEmail && areEmailsMatching(configuredEmail, userEmail)) {
            console.log(`Usuario coincide con email configurado en Aprobadores (${configuredEmail}) - Retornando true`);
            return true;
          }
        } catch (err) {
          console.error('Error verificando email configurado en projectsApi:', err);
        }
      }

      // 5. Para usuarios con rol APROBADOR o GESTOR, verificar también asignaciones en useractas (legacy / manual)
      if (hasRole(userRole, ROLES.APROBADOR) || hasRole(userRole, ROLES.GESTOR)) {
        try {
          let userData = null;
          if (userId) {
            const userRef = doc(db, 'useractas', userId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              userData = userSnap.data();
            }
          }

          if (!userData) {
            const usersRef = collection(db, 'useractas');
            const q = query(usersRef, where('email', '==', userEmail));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
              userData = querySnapshot.docs[0].data();
            }
          }

          if (userData) {
            const allowedProjects = userData.allowedProjects || [];
            const allowedContractProjects = userData.allowedContractProjects || [];

            // Verificar combinaciones contrato/proyecto
            if (allowedContractProjects.length > 0) {
              if (acta.projectName && allowedContractProjects.some(combo => combo.trim() === acta.projectName.trim())) {
                return true;
              }

              for (const project of actaProjects) {
                if (actaProducts.length > 0) {
                  for (const product of actaProducts) {
                    const combinationKey = `${project.trim()}|${product.trim()}`;
                    if (allowedContractProjects.includes(combinationKey)) {
                      return true;
                    }
                  }
                } else {
                  const combinationKey = `${project.trim()}|Sin producto específico`;
                  if (allowedContractProjects.includes(combinationKey)) {
                    return true;
                  }
                }
              }
            }

            // Fallback: Verificar si el proyecto del acta está en allowedProjects
            if (allowedProjects.length > 0 && acta.tipoProyecto && allowedProjects.includes(acta.tipoProyecto)) {
              return true;
            }
          }
        } catch (error) {
          console.error('Error al verificar proyectos del usuario:', error);
        }
      }

      return false;
    } catch (error) {
      console.error('Error al verificar permisos de aprobación:', error);
      return false;
    }
  };

  // Función para aprobar un acta
  const handleApproveActa = async (acta) => {
    try {
      // Mostrar confirmación antes de aprobar
      const confirmApproval = window.confirm('¿Está seguro de aprobar esta acta?');
      if (!confirmApproval) {
        return;
      }
      
      // Verificar permisos antes de aprobar
       const canApprove = await canUserApproveActa(acta, userEmail, userRole);
      
      if (!canApprove) {
        alert('No tienes permisos para aprobar esta acta. Solo puedes aprobar actas asignadas específicamente a tu email.');
        return;
      }
      
      const success = await approveActa(acta.id, userId);
      if (success) {
        // Actualizar el estado local del acta
        const updatedActa = {
          ...acta,
          approvalStatus: 'aprobado',
          approvedBy: userId,
          approvedAt: new Date()
        };
        
        // Actualizar permisos de aprobación
        setApprovalPermissions(prev => ({
          ...prev,
          [acta.id]: false // Ya no se puede aprobar porque está aprobada
        }));
        
        // Notificar al componente padre sobre la actualización
        if (onActaUpdate) {
          onActaUpdate(updatedActa);
        }
        
        // Forzar re-renderizado
        setFilters({...filters});
        alert('Acta aprobada correctamente');
      } else {
        alert('No se pudo aprobar el acta');
      }
    } catch (error) {
      console.error('Error al aprobar acta:', error);
      alert('Error al aprobar el acta: ' + error.message);
    }
  };
  

  
  // Manejar cambios en las fechas
  const handleDateChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Limpiar todos los filtros
  const handleClearFilters = () => {
    setFilters({
      aprobador: '',
      tipoActa: '',
      proyecto: '',
      producto: '',
      fechaInicio: null,
      fechaFin: null,
      approvalStatus: ''
    });
    setShowPendingApproval(false);
  };
  
  // Funciones para manejar firmas
  const handleOpenSignatureManager = (acta) => {
    setSelectedActaForSignature(acta);
    setSignatureModalOpen(true);
  };

  const handleCloseSignatureManager = () => {
    setSignatureModalOpen(false);
    setSelectedActaForSignature(null);
  };

  const handleSignatureUpdate = (updatedAsistentes) => {
    // Actualizar el acta con las nuevas firmas
    if (selectedActaForSignature && onActaUpdate) {
      const updatedActa = {
        ...selectedActaForSignature,
        asistentes: updatedAsistentes
      };
      onActaUpdate(updatedActa);
    }
    
  };
  
  // Filtrar actas según el rol del usuario
  const getBaseActas = useMemo(async () => {
    let filteredActas = [];
    
    if (showAllActas) {
      // Si es APROBADOR, mostrar actas de proyectos asignados + actas creadas por el usuario
      if (hasRole(userRole, ROLES.APROBADOR) && !hasRole(userRole, ROLES.GESTOR)) {
        try {
          const userRef = doc(db, 'useractas', userId);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const allowedProjects = userData.allowedProjects || [];
            const allowedContractProjects = userData.allowedContractProjects || [];
            

             filteredActas = actas.filter((acta, index) => {
               // Incluir actas creadas por el usuario logueado
               if (acta.creatorId === userId) {
                 return true;
               }
               
               // Verificar proyectos legacy
               if (allowedProjects.includes(acta.tipoProyecto)) {

                 return true;
               }
               
               // Verificar combinaciones contrato/proyecto específicas
               if (allowedContractProjects.length > 0) {
                 // Primero verificar si el projectName del acta coincide directamente
                 if (acta.projectName) {
                   const normalizedActaProjectName = acta.projectName.trim();
                   const hasMatch = allowedContractProjects.some(combo => 
                     combo.trim() === normalizedActaProjectName
                   );
                   if (hasMatch) {
                     return true;
                   }
                 }
                 
                 // Si no hay projectName o no coincide, usar la lógica anterior
                 const actaProjects = acta.tipoProyectos || (acta.tipoProyecto ? [acta.tipoProyecto] : []);
                 const actaProducts = acta.productosProyecto || (acta.productoProyecto ? [acta.productoProyecto] : []);
                 
                 // Función para limpiar nombres de proyectos duplicados
                 const cleanProjectName = (projectName) => {
                   if (!projectName) return projectName;
                   
                   // Caso 1: Si el proyecto contiene "/" seguido del mismo texto, remover la duplicación
                   const parts = projectName.split('/');
                   if (parts.length === 2 && parts[0].includes('|') && parts[1].trim() === parts[0].split('|')[1]?.trim()) {
                     return parts[0]; // Retornar solo la primera parte sin duplicación
                   }
                   
                   // Caso 2: Si el proyecto ya contiene el formato correcto contrato|proyecto, usarlo directamente
                   if (projectName.includes('|') && !projectName.includes('/')) {
                     return projectName;
                   }
                   
                   return projectName;
                 };
                 
                 // Limpiar también los proyectos del acta antes de la comparación
                  const cleanedActaProjects = actaProjects.map(project => {
                    return cleanProjectName(project?.trim());
                  });
                 
                 for (const cleanedProject of cleanedActaProjects) {
                   if (actaProducts.length > 0) {
                       for (const product of actaProducts) {
                         const combinationKey = `${cleanedProject}|${product.trim()}`;
                         if (allowedContractProjects.includes(combinationKey)) {
                           return true;
                         }
                       }
                     } else {
                       const combinationKey = `${cleanedProject}|Sin producto específico`;
                       if (allowedContractProjects.includes(combinationKey)) {
                         return true;
                       }
                     }
                 }
               }
               

               return false;
             });
             

             if (filteredActas.length > 0) {
             } else {
             }
          } else {
            // Si no existe userData, al menos mostrar las actas creadas por el usuario
            filteredActas = actas.filter(acta => acta.creatorId === userId);
          }
        } catch (error) {
          console.error('Error al obtener proyectos del usuario APROBADOR:', error);
          // En caso de error, al menos mostrar las actas creadas por el usuario
          filteredActas = actas.filter(acta => acta.creatorId === userId);
        }
      } else {
        // Para otros roles (ADMIN, GESTOR, etc.), mostrar todas las actas
        filteredActas = actas;
      }
    } else {
      filteredActas = actas.filter(acta => acta.creatorId === userId);
    }
    
    // Filtrar borradores: mostrar borradores al creador o si es admin/gestor
    return filteredActas.filter(acta => {
      if (acta.approvalStatus === 'borrador') {
        return acta.creatorId === userId || hasRole(userRole, ROLES.ADMIN) || hasRole(userRole, ROLES.GESTOR);
      }
      return true;
    });
  }, [showAllActas, actas, userId, userRole]);
  
  const [baseActas, setBaseActas] = useState([]);
  
  useEffect(() => {
    const loadBaseActas = async () => {
      const result = await getBaseActas;
      setBaseActas(result);
    };
    loadBaseActas();
  }, [getBaseActas]);


  
  // Aplicar filtros a las actas
  const applyFilters = (actas) => {
    return actas.filter(acta => {
      // Filtro por aprobador (antes responsable)
      if (filters.aprobador) {
        // Verificar si el aprobador está en encargadosAprobacion o customProjectEmail
        const actaAprobadores = [];
        if (acta.encargadosAprobacion && acta.encargadosAprobacion.length > 0) {
          acta.encargadosAprobacion.forEach(e => actaAprobadores.push(e.email));
        }
        if (acta.customProjectEmail) {
          actaAprobadores.push(acta.customProjectEmail);
        }
        
        if (!actaAprobadores.includes(filters.aprobador)) {
          return false;
        }
      }
      
      // Filtro por tipo de acta
      if (filters.tipoActa) {
        if (filters.tipoActa === 'Reunión') {
          if (acta.tipoActa !== 'REUNION') return false;
        } else if (filters.tipoActa === 'Comité') {
          if (acta.tipoActa !== 'COMITE' && !acta.tipoActa.startsWith('COMITE')) return false;
        } else if (filters.tipoActa === 'Otro') {
          // Para "Otro", incluimos todo lo que NO sea REUNION ni COMITE
          // Esto capturará "OTRO" y cualquier categoría personalizada (ej: "2 car")
          const isReunion = acta.tipoActa === 'REUNION';
          const isComite = acta.tipoActa === 'COMITE' || acta.tipoActa.startsWith('COMITE');
          
          if (isReunion || isComite) return false;
        }
      }
      
      // Filtro por proyecto
      if (filters.proyecto && acta.tipoProyecto !== filters.proyecto) {
        return false;
      }
      
      // Filtro por producto (solo si hay proyecto seleccionado)
      if (filters.proyecto && filters.producto && acta.productoProyecto !== filters.producto) {
        return false;
      }
      
      // Filtrar por estado de aprobación
      if (filters.approvalStatus && acta.approvalStatus !== filters.approvalStatus) {
        return false;
      }
      
      // Filtro por rango de fechas
      if (filters.fechaInicio && filters.fechaFin) {
        // Validar que la fecha de inicio no sea posterior a la fecha final
        if (filters.fechaInicio > filters.fechaFin) {
          return true; // No filtrar si las fechas están invertidas
        }
        
        const actaDate = acta.fecha;
        // Ajustar para incluir todo el día seleccionado
        const startDate = new Date(filters.fechaInicio);
        startDate.setHours(0, 0, 0, 0);
        
        const endDate = new Date(filters.fechaFin);
        endDate.setHours(23, 59, 59, 999);
        
        if (!isWithinInterval(actaDate, { 
          start: startDate, 
          end: endDate 
        })) {
          return false;
        }
      } else if (filters.fechaInicio && !filters.fechaFin) {
        const actaDate = acta.fecha;
        const startDate = new Date(filters.fechaInicio);
        startDate.setHours(0, 0, 0, 0);
        if (actaDate < startDate) {
          return false;
        }
      } else if (!filters.fechaInicio && filters.fechaFin) {
        const actaDate = acta.fecha;
        const endDate = new Date(filters.fechaFin);
        endDate.setHours(23, 59, 59, 999);
        if (actaDate > endDate) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Aplicar filtros a las actas usando useMemo
  const filteredActas = useMemo(() => {
    return applyFilters(baseActas);
  }, [baseActas, filters]);

  // Aplicar paginación a las actas filtradas
  const paginatedActas = useMemo(() => {
    return filteredActas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredActas, page, rowsPerPage]);

  return (
    <Paper elevation={3} sx={{...styles.container}}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={styles.title}>
          Historial de actas
        </Typography>
        <Box sx={styles.filtersContainer}>
          <Button 
            variant="contained" 
            color="success" 
            onClick={exportToExcel}
            size={isMobile ? "small" : "medium"}
            startIcon={<FileDownloadIcon />}
            sx={styles.exportButton}
          >
            Exportar Excel
          </Button>
          {/* Mostrar switch de actas por aprobar para usuarios con rol de gestor, aprobador o superior */}
          {(hasRole(userRole, ROLES.GESTOR) || hasRole(userRole, ROLES.APROBADOR)) && (
            <FormControlLabel
              control={
                <Switch
                  checked={showPendingApproval}
                  onChange={handlePendingApprovalChange}
                  color="primary"
                />
              }
              label="Por aprobar"
              sx={{ mr: 1 }}
            />
          )}
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => setShowFilters(!showFilters)}
            size={isMobile ? "small" : "medium"}
            startIcon={<FilterListIcon />}
          >
            Filtros
          </Button>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => setShowAllActas(!showAllActas)}
            size={isMobile ? "small" : "medium"}
          >
            {showAllActas ? 'Ver mis actas' : 'Ver todas'}
          </Button>
        </Box>
      </Box>
      
      {/* Panel de filtros */}
      <Collapse in={showFilters} timeout="auto" unmountOnExit>
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={500} color={'#222'}>Filtros</Typography>
            <IconButton size="small" onClick={handleClearFilters}>
              <ClearIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Limpiar</Typography>
            </IconButton>
          </Box>
          
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Grid container spacing={2}>
              {/* Filtro por aprobador */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="aprobador-label">Aprobador</InputLabel>
                  <Select
                    labelId="aprobador-label"
                    name="aprobador"
                    value={filters.aprobador}
                    label="Aprobador"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value=""><em>Todos</em></MenuItem>
                    {aprobadores.map((aprob) => (
                      <MenuItem key={aprob} value={aprob}>{aprob}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Filtro por tipo de acta */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="tipo-acta-label">Tipo de Acta</InputLabel>
                  <Select
                    labelId="tipo-acta-label"
                    name="tipoActa"
                    value={filters.tipoActa}
                    label="Tipo de Acta"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value=""><em>Todos</em></MenuItem>
                    {tiposActa.map((tipo) => (
                      <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Filtro por proyecto */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="proyecto-label">Contrato</InputLabel>
                  <Select
                    labelId="proyecto-label"
                    name="proyecto"
                    value={filters.proyecto}
                    label="Contrato"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value=""><em>Todos</em></MenuItem>
                    {proyectos.map((proyecto) => (
                      <MenuItem key={proyecto} value={proyecto}>{proyecto}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Filtro por proyecto (solo si hay contrato seleccionado) */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" disabled={!filters.proyecto}>
                  <InputLabel id="producto-label">Proyecto</InputLabel>
                  <Select
                    labelId="producto-label"
                    name="producto"
                    value={filters.producto}
                    label="Proyecto"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value=""><em>Todos</em></MenuItem>
                    {productos.map((producto) => (
                      <MenuItem key={producto} value={producto}>{producto}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Filtros de fecha */}
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <DatePicker
                    label="Desde"
                    value={filters.fechaInicio}
                    onChange={(newValue) => handleDateChange('fechaInicio', newValue)}
                    slotProps={{ 
                      textField: { 
                        size: 'small', 
                        fullWidth: true,
                        helperText: filters.fechaInicio && filters.fechaFin && filters.fechaInicio > filters.fechaFin ? 
                          "La fecha inicial no puede ser posterior a la fecha final" : ""
                      } 
                    }}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <DatePicker
                    label="Hasta"
                    value={filters.fechaFin}
                    onChange={(newValue) => handleDateChange('fechaFin', newValue)}
                    slotProps={{ 
                      textField: { 
                        size: 'small', 
                        fullWidth: true,
                        helperText: filters.fechaInicio && filters.fechaFin && filters.fechaInicio > filters.fechaFin ? 
                          "La fecha final no puede ser anterior a la fecha inicial" : ""
                      } 
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Paper>
      </Collapse>
      <TableContainer sx={{...styles.tableContainer,  transform: 'scale(0.91)', width: '110%', transformOrigin: 'left'}}>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow sx={styles.headerRow}>
              <TableCell sx={styles.headerCellHiddenMobile}>Número de Acta</TableCell>
              <TableCell sx={styles.headerCell}>Tipo</TableCell>
              <TableCell sx={styles.headerCell}>Fecha</TableCell>
              <TableCell sx={styles.headerCellHiddenTablet}>Contrato/Proyecto</TableCell>
              <TableCell sx={styles.headerCellHiddenMobile}>Responsable</TableCell>
              <TableCell sx={styles.headerCellHiddenMobile}>Aprobador</TableCell>
              <TableCell sx={styles.headerCellHiddenMobile}>Creado por</TableCell>
              <TableCell sx={styles.headerCellHiddenMobile}>Fecha creación</TableCell>
              <TableCell sx={styles.headerCellHiddenMobile}>Estado</TableCell>
              <TableCell sx={styles.headerCellActions}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedActas.map((acta, index) => (
              <TableRow key={index} sx={styles.tableRow}>
                <TableCell sx={styles.cellHiddenMobile}>{acta.numeroActa}</TableCell>
                <TableCell>
                  {acta.tipoActa}
                </TableCell>
                <TableCell>
                  {format(acta.fecha, 'dd/MM/yyyy', { locale: es })}
                </TableCell>
                <TableCell sx={styles.cellHiddenTablet}>
                  {acta.productoProyecto ? `${acta.tipoProyecto}/${acta.productoProyecto}` : acta.tipoProyecto || 'Sin contrato'}
                </TableCell>
                <TableCell sx={styles.cellHiddenMobile}>{acta.responsable}</TableCell>
                <TableCell sx={styles.cellHiddenMobile}>
                  {acta.approvedBy ? (
                    // Si el acta está aprobada, mostrar el email del aprobador
                    acta.encargadosAprobacion && acta.encargadosAprobacion.length > 0 ? 
                      acta.encargadosAprobacion[0].email : 
                      (acta.customProjectEmail || 'No disponible')
                  ) : (
                    // Si no está aprobada, mostrar el responsable asignado
                    acta.customProjectEmail || acta.responsableActa || 'No asignado'
                  )}
                </TableCell>
                <TableCell sx={styles.cellHiddenMobile}>{acta.creatorEmail || 'No disponible'}</TableCell>
                <TableCell sx={styles.cellHiddenMobile}>
                  {acta.createdAt ? format(
                    acta.createdAt instanceof Date ? acta.createdAt : 
                    (acta.createdAt && typeof acta.createdAt.toDate === 'function' ? acta.createdAt.toDate() : new Date()), 
                    'dd/MM/yyyy', 
                    { locale: es }
                  ) : 'No disponible'}
                </TableCell>
                <TableCell sx={styles.cellHiddenMobile}>
                  {acta.isExternal ? (
                    <Chip 
                      label="EXTERNO"
                      sx={{ 
                        bgcolor: '#696cff', 
                        color: 'white'
                      }}
                      size="small"
                    />
                  ) : acta.approvalStatus === 'borrador' ? (
                    <Chip 
                      label="BORRADOR"
                      sx={{ 
                        bgcolor: '#ff9800', 
                        color: 'white'
                      }}
                      size="small"
                    />
                  ) : acta.approvalStatus ? (
                    <Chip 
                      label={acta.approvalStatus === 'aprobado' ? 'APROBADO' : 'POR APROBAR'}
                      sx={acta.approvalStatus === 'aprobado' ? styles.approvedChip : styles.pendingChip}
                      size="small"
                    />
                  ) : 'No disponible'}
                </TableCell>
                <TableCell>
                  <Box sx={styles.actionsContainer}>
                    {/* Botón de edición - visible para el creador del acta y para administradores, pero NO para actas externas */}
                    {!acta.isExternal && (
                      <Tooltip title="Editar acta">
                        <Button
                          variant="outlined"
                          color="secondary"
                          size={isMobile ? "small" : "medium"}
                          sx={{ p: 0.5, minWidth: 'auto', height: 'auto', border: 'none', '&:hover': { border: 'none', boxShadow: 'none', backgroundColor: 'transparent' } }}
                          onClick={() => onEditActa(acta)}
                          startIcon={<svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 24 24"><g fill="none" stroke="#5a5dff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"><path strokeDasharray="20" strokeDashoffset="20" d="M3 21h18"><animate fill="freeze" attributeName="stroke-dashoffset" dur="0.2s" values="20;0"/></path><path fill="#5a5dff" fillOpacity="0" strokeDasharray="48" strokeDashoffset="48" d="M7 17v-4l10 -10l4 4l-10 10h-4"><animate fill="freeze" attributeName="fill-opacity" begin="1.1s" dur="0.15s" values="0;0.3"/><animate fill="freeze" attributeName="stroke-dashoffset" begin="0.2s" dur="0.6s" values="48;0"/></path><path strokeDasharray="8" strokeDashoffset="8" d="M14 6l4 4"><animate fill="freeze" attributeName="stroke-dashoffset" begin="0.8s" dur="0.2s" values="8;0"/></path></g></svg>}
                        >
                        </Button>
                      </Tooltip>
                    )}

                    {/* Botón de eliminar - visible solo para actas borrador */}
                    {acta.approvalStatus === 'borrador' && (
                      <Tooltip title="Eliminar borrador">
                        <Button
                          variant="outlined"
                          color="error"
                          size={isMobile ? "small" : "medium"}
                          sx={{ p: 0.5, minWidth: 'auto', height: 'auto', border: 'none', '&:hover': { border: 'none', boxShadow: 'none', backgroundColor: 'transparent' } }}
                          onClick={() => {
                             if (window.confirm('¿Está seguro de eliminar este borrador de acta?')) {
                               handleDeleteDraft(acta);
                             }
                           }}
                          startIcon={<DeleteIcon />}
                        >
                        </Button>
                      </Tooltip>
                    )}

                    {/* Botón de aprobación - visible solo para admins/gestores/aprobadores autorizados y cuando el acta está pendiente de aprobación, pero NO para actas externas ni borradores */}
                    {approvalPermissions[acta.id] && (
                      <Tooltip title="Aprobar acta">
                        <Button
                          variant="outlined"
                          color="success"
                          size={isMobile ? "small" : "medium"}
                          sx={{ p: 0.5, minWidth: 'auto', height: 'auto', border: 'none', '&:hover': { border: 'none', boxShadow: 'none', backgroundColor: 'transparent' } }}
                          onClick={() => handleApproveActa(acta)}
                          startIcon={<ThumbUpIcon />}
                        >
                        </Button>
                      </Tooltip>
                    )}

                    {/* Botón de gestión de firmas - NO disponible para actas externas ni borradores ni actas sin firma */}
                    {!acta.isExternal && acta.approvalStatus !== 'borrador' && !acta.sinFirma && (
                      <Tooltip title="Gestionar firmas">
                        <Button
                          variant="outlined"
                          color="primary"
                          size={isMobile ? "small" : "medium"}
                          sx={{ p: 0.5, minWidth: 'auto', height: 'auto', border: 'none', '&:hover': { border: 'none', boxShadow: 'none', backgroundColor: 'transparent' } }}
                          onClick={() => handleOpenSignatureManager(acta)}
                          startIcon={<DriveFileRenameOutlineIcon />}
                        >
                        </Button>
                      </Tooltip>
                    )}

                    {/* Botón de comentarios - visible para administradores, creador del acta, NO para borradores ni actas externas ni actas sin firma */}
                    {!acta.isExternal && acta.approvalStatus !== 'borrador' && !acta.sinFirma && (hasRole(userRole, ROLES.ADMIN) || 
                      acta.creatorId === userId || // Permitir al creador del acta ver comentarios independientemente del rol
                      approvalPermissions[acta.id] // Usar permisos calculados de forma asíncrona
                    ) && (
                      <Tooltip title="Ver comentarios">
                        <Button
                          variant="outlined"
                          color="warning"
                          size={isMobile ? "small" : "medium"}
                          sx={{ p: 0.5, minWidth: 'auto', height: 'auto', border: 'none', '&:hover': { border: 'none', boxShadow: 'none', backgroundColor: 'transparent' } }}
                          onClick={() => handleOpenComments(acta)}
                        >
                          <Badge 
                            badgeContent={acta.comentarios?.filter(c => c.estado === 'pendiente').length || 0}
                            color="error"
                            invisible={!acta.comentarios?.some(c => c.estado === 'pendiente')}
                          >
                            <NotificationsIcon sx={{ color: '#ff9800' }} />
                          </Badge>
                        </Button>
                      </Tooltip>
                    )}



                      {/* Renderizado condicional para actas externas vs normales - NO disponible para borradores */}
                      {acta.approvalStatus !== 'borrador' && (
                        acta.isExternal && (acta.pdfUrl || acta.pdfBase64) ? (
                          // Para actas externas, usar botón directo de descarga
                          <Tooltip title="Descargar PDF">
                            <Button
                              variant="contained"
                              color="primary"
                              size={isMobile ? "small" : "medium"}
                              sx={styles.downloadButton}
                              onClick={() => handlePDFDownload(acta)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 24 24"><path fill="#ef5350" d="M13 9h5.5L13 3.5zM6 2h8l6 6v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2m4.93 10.44c.41.9.93 1.64 1.53 2.15l.41.32c-.87.16-2.07.44-3.34.93l-.11.04l.5-1.04c.45-.87.78-1.66 1.01-2.4m6.48 3.81c.18-.18.27-.41.28-.66c.03-.2-.02-.39-.12-.55c-.29-.47-1.04-.69-2.28-.69l-1.29.07l-.87-.58c-.63-.52-1.2-1.43-1.6-2.56l.04-.14c.33-1.33.64-2.94-.02-3.6a.85.85 0 0 0-.61-.24h-.24c-.37 0-.7.39-.79.77c-.37 1.33-.15 2.06.22 3.27v.01c-.25.88-.57 1.9-1.08 2.93l-.96 1.8l-.89.49c-1.2.75-1.77 1.59-1.88 2.12c-.04.19-.02.36.05.54l.03.05l.48.31l.44.11c.81 0 1.73-.95 2.97-3.07l.18-.07c1.03-.33 2.31-.56 4.03-.75c1.03.51 2.24.74 3 .74c.44 0 .74-.11.91-.3m-.41-.71l.09.11c-.01.1-.04.11-.09.13h-.04l-.19.02c-.46 0-1.17-.19-1.9-.51c.09-.1.13-.1.23-.1c1.4 0 1.8.25 1.9.35M7.83 17c-.65 1.19-1.24 1.85-1.69 2c.05-.38.5-1.04 1.21-1.69zm3.02-6.91c-.23-.9-.24-1.63-.07-2.05l.07-.12l.15.05c.17.24.19.56.09 1.1l-.03.16l-.16.82z"/></svg>
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Descargar PDF">
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={!!generatingPdfActaId}
                              size={isMobile ? "small" : "medium"}
                              sx={styles.downloadButton}
                              onClick={() => handlePDFDownload(acta)}
                            >
                              {generatingPdfActaId === acta.id ? (
                                <CircularProgress size={20} sx={{ color: '#fff' }} />
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 24 24"><path fill="#ef5350" d="M13 9h5.5L13 3.5zM6 2h8l6 6v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2m4.93 10.44c.41.9.93 1.64 1.53 2.15l.41.32c-.87.16-2.07.44-3.34.93l-.11.04l.5-1.04c.45-.87.78-1.66 1.01-2.4m6.48 3.81c.18-.18.27-.41.28-.66c.03-.2-.02-.39-.12-.55c-.29-.47-1.04-.69-2.28-.69l-1.29.07l-.87-.58c-.63-.52-1.2-1.43-1.6-2.56l.04-.14c.33-1.33.64-2.94-.02-3.6a.85.85 0 0 0-.61-.24h-.24c-.37 0-.7.39-.79.77c-.37 1.33-.15 2.06.22 3.27v.01c-.25.88-.57 1.9-1.08 2.93l-.96 1.8l-.89.49c-1.2.75-1.77 1.59-1.88 2.12c-.04.19-.02.36.05.54l.03.05l.48.31l.44.11c.81 0 1.73-.95 2.97-3.07l.18-.07c1.03-.33 2.31-.56 4.03-.75c1.03.51 2.24.74 3 .74c.44 0 .74-.11.91-.3m-.41-.71l.09.11c-.01.1-.04.11-.09.13h-.04l-.19.02c-.46 0-1.17-.19-1.9-.51c.09-.1.13-.1.23-.1c1.4 0 1.8.25 1.9.35M7.83 17c-.65 1.19-1.24 1.85-1.69 2c.05-.38.5-1.04 1.21-1.69zm3.02-6.91c-.23-.9-.24-1.63-.07-2.05l.07-.12l.15.05c.17.24.19.56.09 1.1l-.03.16l-.16.82z"/></svg>
                              )}
                            </Button>
                          </Tooltip>
                        )
                      )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[15, 30, 50]}
        component="div"
        count={filteredActas.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
      {filteredActas.length === 0 && (
        <Box sx={styles.emptyStateContainer}>
          <Typography variant="body1" color="text.secondary">
            No hay actas disponibles
          </Typography>
        </Box>
      )}
      
      {/* Modal de gestión de firmas */}
      <SignatureManager
        open={signatureModalOpen}
        onClose={handleCloseSignatureManager}
        acta={selectedActaForSignature}
        onSignatureUpdate={handleSignatureUpdate}
      />

      {/* Diálogo de comentarios */}
      <Dialog 
        open={commentsDialogOpen} 
        onClose={() => setCommentsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Comentarios - Acta {selectedActaComments?.numeroActa}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedActaComments?.comentarios && selectedActaComments.comentarios.length > 0 ? (
            <List>
              {selectedActaComments.comentarios.map((comment, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {comment.asistente}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip 
                              label={comment.estado.toUpperCase()}
                              size="small"
                              color={
                                comment.estado === 'aprobado' ? 'success' : 
                                comment.estado === 'rechazado' ? 'error' : 'warning'
                              }
                            />
                            {comment.estado === 'pendiente' && (
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                {processingComment ? (
                                  <CircularProgress size={20} sx={{ color: '#1976d2' }} />
                                ) : (
                                  <>
                                    <Tooltip title="Aprobar comentario">
                                      <IconButton 
                                        size="small" 
                                        color="success"
                                        onClick={() => handleApproveComment(comment, selectedActaComments)}
                                        disabled={processingComment}
                                      >
                                        <CheckIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Rechazar comentario">
                                      <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={() => handleRejectComment(comment)}
                                        disabled={processingComment}
                                      >
                                        <CloseIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {comment.comentario}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {comment.fecha && typeof comment.fecha.toDate === 'function' 
                              ? format(comment.fecha.toDate(), 'dd/MM/yyyy HH:mm', { locale: es })
                              : 'Fecha no disponible'
                            }
                          </Typography>
                          {comment.estado === 'rechazado' && comment.razonRechazo && (
                            <Typography variant="body2" color="error" sx={{ mt: 1, fontStyle: 'italic' }}>
                              Razón del rechazo: {comment.razonRechazo}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < selectedActaComments.comentarios.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No hay comentarios para esta acta.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentsDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de rechazo de comentario */}
      <Dialog 
        open={showRejectDialog} 
        onClose={() => !processingComment && setShowRejectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Rechazar Comentario
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            ¿Está seguro de que desea rechazar el comentario de {selectedComment?.asistente}?
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
            "{selectedComment?.comentario}"
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Razón del rechazo"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={processingComment}
            placeholder="Explique por qué no se acepta este comentario..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowRejectDialog(false);
              setRejectReason('');
              setSelectedComment(null);
            }}
            disabled={processingComment}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmReject}
            variant="contained"
            color="error"
            disabled={processingComment || !rejectReason.trim()}
          >
            {processingComment ? 'Procesando...' : 'Rechazar Comentario'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};



export default HistorialActas;
